import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { isTiandituAvailable } from '../components/tianditu/tiandituStyle.js'

// XingRoute web basemap provider state. Mirrors iOS `BasemapStack` but
// inverts the default: web defaults to `mapbox` (richer UX — 3D buildings,
// satellite, monochrome theme), and exposes `tianditu` as a user-selectable
// alternative for users who prefer the China-compliant 国家测绘地理信息局
// basemap. The full compliance posture lives in
// `wiki/decisions/05-basemaps.md` §Compliance posture.
//
// Persistence: `localStorage['xr.basemap.provider']`. Reading is guarded so
// SSR / Node test environments (no `window`) get the default; writing is
// guarded for the same reason.

export type BasemapProvider = 'mapbox' | 'tianditu'

const STORAGE_KEY = 'xr.basemap.provider'
const DEFAULT_PROVIDER: BasemapProvider = 'mapbox'

function readStoredProvider(): BasemapProvider {
  if (typeof window === 'undefined') return DEFAULT_PROVIDER
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === 'mapbox' || raw === 'tianditu') return raw
  } catch {
    // localStorage blocked (private mode, SSR shim) — fall through.
  }
  return DEFAULT_PROVIDER
}

function writeStoredProvider(next: BasemapProvider): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Silently ignore quota / privacy errors.
  }
}

interface BasemapContextValue {
  provider: BasemapProvider
  setProvider: (next: BasemapProvider) => void
  /** True when the build was shipped with VITE_TIANDITU_KEY — caller
   * should hide the TianDiTu option in pickers when this is false. */
  tiandituAvailable: boolean
}

const BasemapContext = createContext<BasemapContextValue | null>(null)

export function BasemapProviderProvider({ children }: { children: ReactNode }) {
  const [provider, setProviderState] = useState<BasemapProvider>(() => readStoredProvider())
  const tiandituAvailable = useMemo(() => isTiandituAvailable(), [])

  const setProvider = useCallback(
    (next: BasemapProvider) => {
      // If tianditu is unavailable at build time, refuse the switch — the
      // resulting style would be empty.
      const safe: BasemapProvider = next === 'tianditu' && !tiandituAvailable ? 'mapbox' : next
      setProviderState(safe)
      writeStoredProvider(safe)
    },
    [tiandituAvailable],
  )

  // Cross-tab sync: another tab may switch provider via Profile UI.
  useEffect(() => {
    if (typeof window === 'undefined') return
    function onStorage(event: StorageEvent) {
      if (event.key !== STORAGE_KEY) return
      const next =
        event.newValue === 'mapbox' || event.newValue === 'tianditu'
          ? event.newValue
          : DEFAULT_PROVIDER
      setProviderState(next)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value = useMemo<BasemapContextValue>(
    () => ({ provider, setProvider, tiandituAvailable }),
    [provider, setProvider, tiandituAvailable],
  )
  return <BasemapContext.Provider value={value}>{children}</BasemapContext.Provider>
}

export function useBasemapProvider(): BasemapContextValue {
  const ctx = useContext(BasemapContext)
  if (!ctx) {
    // Allow consumers (RouteThumbMap, tests, etc.) to call without a
    // provider mounted; default to mapbox + tianditu unavailable.
    return {
      provider: DEFAULT_PROVIDER,
      setProvider: () => {},
      tiandituAvailable: false,
    }
  }
  return ctx
}
